import { invalidateCrmBoardCache } from './crmBoardCache';
import { invalidateListingsCache, listingsCacheKey } from './listingsCache';
import { invalidateSalesCache } from './salesCache';

// Dashboard payload cache. Memory for the current tab, sessionStorage so a
// refresh or account-switch hard-reload can paint Home instantly (stale-
// while-revalidate still refreshes in the background).

const CACHE_TTL_MS = 5 * 60 * 1000;
const SESSION_PREFIX = 'ipm.dash.v2.';

let _cache = {};

function sessionKey(userId) {
  return `${SESSION_PREFIX}${userId}`;
}

function stripDataUrls(value) {
  if (value == null) return value;
  if (typeof value === 'string') {
    return (value.startsWith('data:') && value.length > 200) ? '' : value;
  }
  if (Array.isArray(value)) return value.map(stripDataUrls);
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => { out[k] = stripDataUrls(value[k]); });
    return out;
  }
  return value;
}

function readSession(userId) {
  try {
    const raw = sessionStorage.getItem(sessionKey(userId));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !entry.data || typeof entry.timestamp !== 'number') return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(sessionKey(userId));
      return null;
    }
    return entry;
  } catch (_) {
    return null;
  }
}

function writeSession(userId, data, timestamp) {
  try {
    sessionStorage.setItem(sessionKey(userId), JSON.stringify({
      data: stripDataUrls(data),
      timestamp,
    }));
  } catch (_) {
    try { sessionStorage.removeItem(sessionKey(userId)); } catch (__) {}
  }
}

/**
 * Get cached dashboard data for a user.
 * Returns null if cache is missing or expired.
 */
export function getDashboardCache(userId) {
  if (!userId) return null;
  const id = String(userId);
  const mem = _cache[id];
  if (mem && Date.now() - mem.timestamp <= CACHE_TTL_MS) return mem.data;
  if (mem) delete _cache[id];
  const session = readSession(id);
  if (!session) return null;
  _cache[id] = session;
  return session.data;
}

/**
 * Store dashboard data in cache.
 */
export function setDashboardCache(userId, data) {
  if (!userId || !data) return;
  const id = String(userId);
  const timestamp = Date.now();
  _cache[id] = { data, timestamp };
  writeSession(id, data, timestamp);
}

export const DASHBOARD_INVALIDATED_KEY = 'dashboardInvalidated';

/**
 * Invalidate cache for a user (call after mutations like add-agent, add-lead, property status change, etc.)
 * Uses localStorage so the flag is visible across tabs (e.g. update in one tab, dashboard in another).
 */
export function invalidateDashboardCache(userId) {
  if (userId) {
    const id = String(userId);
    try { localStorage.setItem(DASHBOARD_INVALIDATED_KEY, id); } catch (_) {}
    delete _cache[id];
    try { sessionStorage.removeItem(sessionKey(id)); } catch (_) {}
    invalidateCrmBoardCache(id);
    invalidateListingsCache(listingsCacheKey(id, true));
    invalidateListingsCache(listingsCacheKey(id, false));
    invalidateSalesCache(id);
  } else {
    _cache = {};
    try {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(SESSION_PREFIX)) keys.push(k);
      }
      keys.forEach((k) => sessionStorage.removeItem(k));
    } catch (_) {}
    invalidateCrmBoardCache();
    invalidateListingsCache();
    invalidateSalesCache();
  }
}

/**
 * Check and clear the "dashboard was invalidated" flag (so Dashboard can refetch on mount/focus).
 */
export function takeDashboardInvalidated() {
  try {
    const id = localStorage.getItem(DASHBOARD_INVALIDATED_KEY);
    localStorage.removeItem(DASHBOARD_INVALIDATED_KEY);
    return id || null;
  } catch (_) {
    return null;
  }
}
